import Quill, {type Delta, type Op} from 'quill'
import {html_element} from '@bitstillery/common/lib/dom'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import {present} from '@bitstillery/common/lib/utils'

import type QuillEmbed from 'quill/blots/embed'

// Quill's registry types only a handful of its names.
const Embed = unchecked<typeof QuillEmbed>(Quill.import('blots/embed'))

class PlaceholderBlot extends Embed {
    static override blotName = 'placeholder-token'
    static override className = 'ql-placeholder-token'
    static override tagName = 'span'
    static override scope = Embed.scope

    static override create(value: string) {
        const node = present(html_element(super.create()), 'placeholder blot element')
        node.dataset.placeholder = value
        node.textContent = value
        node.setAttribute('contenteditable', 'false')
        node.style.position = 'relative'
        node.style.display = 'inline'
        node.style.verticalAlign = 'baseline'

        // Check placeholder type and add appropriate class
        const inner_value = value.replaceAll(/^#+|#+$/g, '') // Remove # from start/end
        const plugin = PlaceholderPlugin.current_instance

        // Store the inner value for click handler
        node.dataset.placeholderValue = inner_value

        // Apply correct styling based on placeholder type
        if (plugin) {
            const is_fixed = plugin.get_fixed_placeholders().includes(inner_value)
            const is_existing = plugin.get_existing_placeholders().includes(inner_value)

            if (is_fixed) {
                node.classList.add('ql-placeholder-fixed')
            } else if (is_existing) {
                node.classList.add('ql-placeholder-existing')
            } else {
                node.classList.add('ql-placeholder-new')
            }
        } else {
            // Fallback if plugin not available
            node.classList.add('ql-placeholder-new')
        }

        // Add click event handler
        node.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            const current_plugin = PlaceholderPlugin.current_instance
            if (current_plugin?.options.on_placeholder_click) {
                const clicked_value = node.dataset.placeholderValue || ''

                // Do the lookup now at click time when we know the plugin is available
                const is_fixed = current_plugin.get_fixed_placeholders().includes(clicked_value)
                const existing_objects = current_plugin.get_existing_placeholder_objects()
                const existing_obj = existing_objects.find((p) => p.label === clicked_value)

                let type: 'existing' | 'fixed' | 'new'
                let placeholder_value: number | null = null

                if (is_fixed) {
                    type = 'fixed'
                } else if (existing_obj) {
                    ;({value: placeholder_value} = existing_obj)
                    type = 'existing'
                } else {
                    type = 'new'
                }

                current_plugin.options.on_placeholder_click({
                    label: clicked_value,
                    type: type,
                    value: placeholder_value,
                })
            }
        })

        // Add remove button
        const remove_btn = document.createElement('span')
        remove_btn.innerHTML = '×'
        remove_btn.className = 'ql-placeholder-remove'

        node.append(remove_btn)
        // Show/hide remove button on hover
        node.addEventListener('mouseenter', () => {
            remove_btn.style.display = 'flex'
        })

        node.addEventListener('mouseleave', () => {
            remove_btn.style.display = 'none'
        })

        // Handle remove button click
        remove_btn.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            // Find the blot instance and remove it
            const blot = Quill.find(node)
            // Quill answers with itself for its own root; only a blot can be removed.
            if (blot && !(blot instanceof Quill)) {
                blot.remove()
            }
        })

        return node
    }

    static override formats(node: HTMLElement) {
        return node.dataset.placeholder
    }

    static override value(node: HTMLElement) {
        return node.dataset.placeholder
    }

    // Allow cursor to move after the placeholder
    override length() {
        return 1
    }

    // Make sure the embed can be selected and cursor can move past it
    override index(node: Node, offset: number) {
        if (node === this.domNode) {
            return 0
        }
        return super.index(node, offset)
    }

    // Handle cursor positioning
    override position(index: number, _inclusive = false): [Node, number] {
        if (index === 0) {
            return [this.domNode, 0]
        }
        return [this.parent.domNode, this.parent.children.indexOf(this) + 1]
    }
}

// Clipboard matcher for converting #...# text to placeholder embeds
function register_placeholder_matcher(quill: Quill) {
    quill.clipboard.addMatcher(Node.TEXT_NODE, function (node: Node, delta: Delta) {
        const regex = /#([\w.]+)#/g
        const ops: Op[] = []
        if (!(node instanceof Text)) {
            return delta
        }
        const str = node.data
        let last_index = 0
        let match = regex.exec(str)
        while (match !== null) {
            if (match.index > last_index) {
                ops.push({insert: str.slice(last_index, match.index)})
            }
            // Insert as embed instead of text with attributes
            ops.push({insert: {'placeholder-token': match[0]}})
            last_index = regex.lastIndex
            match = regex.exec(str)
        }
        if (last_index < str.length) {
            ops.push({insert: str.slice(last_index)})
        }
        if (ops.length > 0) {
            delta.ops = ops
        }
        return delta
    })
}

export interface PlaceholderPluginOptions {
    placeholders_existing?: {value: number; label: string}[]
    placeholders_fixed?: string[]
    on_placeholder_click?: (placeholder: {value: number | null; label: string; type: 'existing' | 'fixed' | 'new'}) => void
}

export class PlaceholderPlugin {
    private quill: Quill
    public options: PlaceholderPluginOptions
    static current_instance: PlaceholderPlugin | null = null

    constructor(quill: Quill, options: PlaceholderPluginOptions = {}) {
        this.quill = quill
        this.options = options

        // Store current instance for static access
        PlaceholderPlugin.current_instance = this

        // Register the placeholder blot
        Quill.register('formats/placeholder-token', PlaceholderBlot)

        // Register clipboard matcher
        register_placeholder_matcher(quill)
    }

    // Method to get existing placeholders (returns just the labels for comparison)
    get_existing_placeholders() {
        return (this.options.placeholders_existing || []).map((p) => p.label)
    }

    // Method to get full existing placeholder objects
    get_existing_placeholder_objects() {
        return this.options.placeholders_existing || []
    }

    get_fixed_placeholders() {
        return this.options.placeholders_fixed || []
    }

    // Method to manually convert placeholders in text
    convert_placeholders(): void {
        const text = this.quill.getText()
        const regex = /#[\w.]+#/g
        let match = regex.exec(text)

        while (match !== null) {
            const start = match.index
            const {length} = match[0]

            // Check if this text position contains plain text (not already an embed)
            const actual_text = this.quill.getText(start, length)
            if (actual_text === match[0]) {
                // Check what's at this position
                const [leaf] = this.quill.getLeaf(start)

                // If it's not already a placeholder embed, convert it
                if (!(leaf instanceof PlaceholderBlot)) {
                    this.quill.deleteText(start, length)
                    this.quill.insertEmbed(start, 'placeholder-token', match[0])
                    // After modifying text, we need to restart the search
                    return this.convert_placeholders()
                }
            }
            match = regex.exec(text)
        }
    }

    // Method to get clean HTML with placeholders converted back to #...# text
    get_clean_html() {
        let html = this.quill.getSemanticHTML()
        if (html === '<p></p>') {
            html = ''
        }

        // Convert placeholder embeds back to plain text
        // The HTML structure for embeds might be different, so let's handle multiple patterns
        html = html.replaceAll(
            /<span[^>]*class="[^"]*ql-placeholder-token[^"]*"[^>]*data-placeholder="([^"]*)"[^>]*>.*?<\/span><\/span>.<\/span>/g,
            '$1',
        )
        html = html.replaceAll(
            /<span[^>]*data-placeholder="([^"]*)"[^>]*class="[^"]*ql-placeholder-token[^"]*"[^>]*>.*?<\/span><\/span>.<\/span>/g,
            '$1',
        )

        return html
    }
}
